import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { SwipableBox } from '@eneco/custom-components/SwipableBox/SwipableBox';
import { Box, Divider, NavLink, PageGrid, Stack, Text } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { ChevronRightIcon, HomeIcon } from '@eneco/sparky/icons';

import type { BreadCrumbsContent } from './bread-crumbs-content.types';

/**
 * `BreadCrumbs` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/Breadcrumbs/Breadcrumbs.tsx`): a
 * swipeable trail of links with a home icon on the first crumb and a chevron between each pair, the last
 * crumb rendered as plain (non-link) text, wrapped in a `PageGrid` with a leading `Divider` on desktop
 * only.
 *
 * Known simplification: the legacy component's link uses `@sitecore/common`'s `useLinkComponent` with
 * `currentMatch="exact"`, which highlights a crumb matching the current route. This lane's `<NavLink>`
 * renders every non-last crumb identically instead — see `bread-crumbs-content.types.ts` for the larger
 * simplification (no Sanity content source for the trail exists yet at all).
 */
const BreadCrumbs: FC = () => {
  const content = useContent<BreadCrumbsContent>();
  const isDesktop = useMediaQuery('lg');
  const paddingX = isDesktop ? '0' : '3';

  const crumbs = content.breadCrumbs ?? content.items ?? [];
  if (crumbs.length === 0) return null;

  const trail = (
    <SwipableBox as="nav" paddingY="3" paddingLeft={paddingX}>
      <Stack direction="row" alignY="center" as="ul" gap="1">
        {crumbs.map((breadCrumb, i) => (
          <Text as="li" key={breadCrumb.url} whiteSpace="nowrap">
            <Stack direction="row" gap="1">
              {i === crumbs.length - 1 ? (
                <Box paddingRight={paddingX}>
                  <Text size="BodyS" color="textLowEmphasis">
                    {breadCrumb.name}
                  </Text>
                </Box>
              ) : (
                <>
                  <NavLink href={breadCrumb.url} variant="tertiary" leftIcon={i === 0 ? <HomeIcon /> : undefined}>
                    {breadCrumb.name}
                  </NavLink>
                  <ChevronRightIcon color="iconSecondary" />
                </>
              )}
            </Stack>
          </Text>
        ))}
      </Stack>
    </SwipableBox>
  );

  return isDesktop ? (
    <PageGrid>
      <PageGrid.Item gridColumn="1/-1">
        <Divider />
        {trail}
      </PageGrid.Item>
    </PageGrid>
  ) : (
    trail
  );
};

export default BreadCrumbs;
